import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { Boxes } from 'src/app/models/Boxes.models';
import { OrdersService } from 'src/app/services/orders.service';
import { OpenModalService } from 'src/app/services/shared/open-modal.service';

/**
 * Sub-modale "Aggiungi scatola" — apre quando l'utente clicca "+ Aggiungi
 * collo" dentro `<app-confirm-boxes>` (step #3 del wizard preparazione).
 *
 * Restyle v2.11.1 (#5):
 *  - 🚨 Drop kendo-dialog wrapper + accordion bootstrap (data-bs-toggle
 *    NON funziona perché Bootstrap JS non è importato → l'accordion era
 *    sempre aperto/inert).
 *  - 🚨 Drop kendo-dropdownlist quantità → native <select>.
 *  - 🚨 Drop `[(ngModel)]="chosenBox.box_type_id"` su un `<div>`
 *    (ngModel valido solo su input/select/textarea → no-op).
 *  - 🚨 Drop 6 console.log + 1 FormGroup mai usata.
 *  - 🚨 Drop `id="flexRadioDefault1"` duplicato (HTML invalido — più
 *    radio con stesso id).
 *  - 🚨 Drop Swal interno (il parent confirm-boxes gestisce il feedback
 *    di completion).
 *  - Switch a `inject()` + `takeUntilDestroyed`.
 *  - Pattern Diakonix modal-style nested z 2200 (sopra confirm-boxes 2100).
 */
@Component({
  selector: 'app-add-box',
  templateUrl: './add-box.component.html',
  styleUrls: ['./add-box.component.scss'],
  standalone: false,
})


export class AddBoxComponent implements OnInit {

  @Input() box_type_id: string = '-1';
  @Input() openBox = false;
  @Output() dataInfo = new EventEmitter<object>();

  private readonly destroyRef = inject(DestroyRef);
  private readonly orderService = inject(OrdersService);
  public readonly openModal = inject(OpenModalService);
  private readonly cdr = inject(ChangeDetectorRef);

  /** Scatole standard caricate dal BE. */
  public boxes: Boxes.Box[] = [];

  /** Form state della scatola custom. */
  public newBox: Boxes.BoxOrder = new Boxes.BoxOrder(null);

  /**
   * `box_type_id`:
   *   -2 = niente selezionato (default)
   *   -1 = custom
   *   >0 = id di una scatola standard
   */
  public chosenBox: Boxes.Box = {
    box_type_id: -2,
    box_type_desc: '',
    isDeleted: false,
  };

  /** Quantità colli selezionata (1..10). */
  public selectedNumber = 0;
  public readonly maxBoxes = 10;
  public readonly numbers: number[] = Array.from(
    { length: 10 },
    (_, i) => i + 1,
  );

  ngOnInit(): void {
    this.orderService
      .getBox()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.boxes = result ?? [];
        this.cdr.detectChanges();
      });
  }

  closeBox(): void {
    this.openModal.closeBox(null);
  }

  /**
   * Selezione di una scatola — standard (id>0) o custom (id=-1).
   */
  changeBox(id: number, desc = ''): void {
    this.chosenBox = { ...this.chosenBox, box_type_id: id, box_type_desc: desc };
  }

  /** Helper template — true se l'utente ha selezionato la scatola con `id`. */
  isSelected(id: number): boolean {
    return this.chosenBox.box_type_id === id;
  }

  /** Bottone Salva disabled finché form non è valido. */
  get isSaveDisabled(): boolean {
    if (!this.selectedNumber) return true;
    if (this.chosenBox.box_type_id === -2) return true;
    if (this.chosenBox.box_type_id === -1 && this.newBox.checkForNull()) {
      return true;
    }
    return false;
  }

  /**
   * Conferma scelta → emette payload al parent (confirm-boxes.addBox).
   */
  validateBox(): void {
    // Coerce -1 (custom) a null per backward compat con il consumer
    // legacy parent (era così anche pre-restyle).
    const normalBox: Boxes.Box = {
      ...this.chosenBox,
      box_type_id:
        this.chosenBox.box_type_id < 0 ? null : this.chosenBox.box_type_id,
    } as Boxes.Box;

    const payload = {
      isCustom: this.chosenBox.box_type_id === -1,
      normal: normalBox,
      custom: this.newBox,
      boxesNo: this.selectedNumber,
    };

    this.openModal.closeBox(payload);
    this.dataInfo.emit(payload);
  }
  
}
